// src/components/Wizard/QuestionWizard.jsx
import React, { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiCheck } = FiIcons

const QuestionWizard = ({ lessonDesign, onComplete }) => {
  const [answers, setAnswers] = useState({
    year_level: lessonDesign.year_level || '',
    is_series: lessonDesign.is_series || false,
    lesson_count: lessonDesign.lesson_count || 1,
    // Default to 50 mins if not set:
    lesson_duration_minutes: lessonDesign.lesson_duration_minutes ?? 50,
    activity_preferences: lessonDesign.activity_preferences || [],
    stimulus_usage: lessonDesign.stimulus_usage || '',
    set_homework: lessonDesign.set_homework || false,
    technology: lessonDesign.technology || '',
    wants_worksheets: lessonDesign.wants_worksheets || false,
    wants_slides: lessonDesign.wants_slides || false,
    other_considerations: lessonDesign.other_considerations || '',
  })
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [touched, setTouched] = useState({})
  const [finishing, setFinishing] = useState(false)

  // Ensure we start at the top of the page when this wizard mounts
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [])

  const questions = [
    {
      id: 'year_level',
      title: 'What year level is the lesson for?',
      type: 'select', // still "select" for validation, but rendered as badges
      options: [
        'Kindergarten',
        'Year 1',
        'Year 2',
        'Year 3',
        'Year 4',
        'Year 5',
        'Year 6',
        'Year 7',
        'Year 8',
        'Year 9',
        'Year 10',
        'Year 11',
        'Year 12',
      ],
      required: true,
    },
    {
      id: 'series_type',
      title: 'Is this a single lesson or a series of lessons?',
      type: 'radio',
      options: [
        { value: false, label: 'Single lesson' },
        { value: true, label: 'Series of lessons' },
      ],
      required: true,
    },
    {
      id: 'lesson_count',
      title: 'How many lessons in the series?',
      type: 'number',
      min: 2,
      max: 20,
      showIf: (answers) => answers.is_series,
      required: true,
    },
    {
      id: 'lesson_duration_minutes',
      title: 'How long is each lesson (in minutes)?',
      type: 'number',
      min: 10,
      max: 240,
      required: true,
    },
    {
      id: 'activity_preferences',
      title: 'Choose the types of activities you want to include:',
      type: 'checkbox',
      options: [
        'Teacher instruction',
        'Small group work',
        'Pair work',
        'Individual work',
        'Discussion',
        'Practical work',
        'A varied combination that works for the content',
      ],
      required: true,
      minSelections: 1,
    },
    {
      id: 'stimulus_usage',
      title: 'How do you want the stimulus to be used?',
      type: 'radio',
      options: [
        { value: 'pre_reading', label: 'Pre-reading activity' },
        { value: 'in_lesson_reading', label: 'Read during the lesson' },
        { value: 'explicit_instruction', label: 'Explicit instruction material' },
        { value: 'idea_only', label: 'Use the ideas only (not the text itself)' },
      ],
      required: true,
    },
    {
      id: 'set_homework',
      title: 'Should a homework task be set?',
      type: 'radio',
      options: [
        { value: true, label: 'Yes, include homework' },
        { value: false, label: 'No homework needed' },
      ],
      required: true,
    },
    {
      id: 'technology',
      title: 'Do you want to use technology? If yes, what technology?',
      type: 'text',
      placeholder: 'e.g., iPads, interactive whiteboard, laptops, or leave blank for none',
      required: false,
    },
    {
      id: 'wants_worksheets',
      title: 'Do you want worksheets produced for the tasks?',
      type: 'radio',
      options: [
        { value: true, label: 'Yes, create worksheets' },
        { value: false, label: 'No worksheets needed' },
      ],
      required: true,
    },
    {
      id: 'wants_slides',
      title: 'Would you like a slideshow for the lesson?',
      type: 'radio',
      options: [
        { value: true, label: 'Yes, create slideshow' },
        { value: false, label: 'No slideshow needed' },
      ],
      required: true,
    },
    {
      id: 'other_considerations',
      title: 'Is there anything else you want me to take into account?',
      type: 'textarea',
      placeholder: 'Any special considerations, learning needs, or preferences...',
      required: false,
    },
  ]

  const visibleQuestions = questions.filter((q) => !q.showIf || q.showIf(answers))

  const getAnswerValue = (question) => {
    if (question.id === 'series_type') {
      return answers.is_series
    }
    return answers[question.id]
  }

  const validateQuestion = (question, value) => {
    if (!question.required) return true

    if (question.id === 'series_type') {
      return value === true || value === false
    }

    if (question.type === 'checkbox') {
      return Array.isArray(value) && value.length >= (question.minSelections || 1)
    }

    if (question.type === 'number') {
      if (value === '' || value === null || value === undefined) return false
      const num = Number(value)
      if (Number.isNaN(num)) return false
      if (question.min !== undefined && num < question.min) return false
      if (question.max !== undefined && num > question.max) return false
      return true
    }

    if (question.type === 'select' || question.type === 'text' || question.type === 'textarea') {
      return value !== '' && value !== null && value !== undefined
    }

    if (question.type === 'radio') {
      return value !== '' && value !== null && value !== undefined
    }

    return true
  }

  const countAnswered = () => {
    let count = 0
    for (const q of visibleQuestions) {
      const value = getAnswerValue(q)
      if (validateQuestion(q, value)) {
        count++
      }
    }
    return count
  }

  const totalQuestions = visibleQuestions.length
  const answeredCount = countAnswered()
  const allValid = answeredCount === totalQuestions

  const persistAnswers = async (newAnswers) => {
    setSaving(true)
    setSaveError('')
    try {
      const payload = {
        year_level: newAnswers.year_level || null,
        is_series: newAnswers.is_series,
        lesson_count: newAnswers.is_series ? newAnswers.lesson_count : null,
        lesson_duration_minutes: newAnswers.lesson_duration_minutes
          ? Number(newAnswers.lesson_duration_minutes)
          : null,
        activity_preferences: newAnswers.activity_preferences,
        stimulus_usage: newAnswers.stimulus_usage || null,
        set_homework: newAnswers.set_homework,
        technology: newAnswers.technology || null,
        wants_worksheets: newAnswers.wants_worksheets,
        wants_slides: newAnswers.wants_slides,
        other_considerations: newAnswers.other_considerations || null,
        raw_answers_json: newAnswers,
        updated_at: new Date().toISOString(),
      }

      const { error } = await supabase
        .from('lesson_designs')
        .update(payload)
        .eq('id', lessonDesign.id)

      if (error) throw error
    } catch (error) {
      console.error('Error auto-saving answers:', error)
      setSaveError('Failed to auto-save. Your latest change might not be saved.')
    } finally {
      setSaving(false)
    }
  }

  const handleAnswerChange = (question, rawValue) => {
    setAnswers((prev) => {
      const updated = { ...prev }

      if (question.id === 'series_type') {
        const boolVal = rawValue === true || rawValue === 'true'
        updated.is_series = boolVal
        if (!boolVal) {
          updated.lesson_count = 1
        }
      } else if (question.type === 'number') {
        const num = rawValue === '' ? '' : Number(rawValue)
        updated[question.id] = Number.isNaN(num) ? '' : num
      } else if (question.type === 'checkbox') {
        updated[question.id] = rawValue
      } else {
        updated[question.id] = rawValue
      }
      return updated
    })
    setTouched((prev) => ({ ...prev, [question.id]: true }))
  }

  // Auto-save whenever answers change (after initial mount)
  useEffect(() => {
    if (!lessonDesign?.id) return
    persistAnswers(answers)
    // removed unused eslint-disable directive
  }, [answers, lessonDesign?.id])

  const handleFinish = async () => {
    if (!allValid) return
    setFinishing(true)
    setSaveError('')

    try {
      const payload = {
        year_level: answers.year_level || null,
        is_series: answers.is_series,
        lesson_count: answers.is_series ? answers.lesson_count : null,
        lesson_duration_minutes: answers.lesson_duration_minutes
          ? Number(answers.lesson_duration_minutes)
          : null,
        activity_preferences: answers.activity_preferences,
        stimulus_usage: answers.stimulus_usage || null,
        set_homework: answers.set_homework,
        technology: answers.technology || null,
        wants_worksheets: answers.wants_worksheets,
        wants_slides: answers.wants_slides,
        other_considerations: answers.other_considerations || null,
        raw_answers_json: answers,
        status: 'plan_generated',
        updated_at: new Date().toISOString(),
      }

      const { error } = await supabase
        .from('lesson_designs')
        .update(payload)
        .eq('id', lessonDesign.id)

      if (error) throw error

      if (onComplete) {
        onComplete({ ...lessonDesign, ...payload })
      }
    } catch (error) {
      console.error('Error updating lesson design:', error)
      setSaveError('Failed to save answers. Please try again.')
    } finally {
      setFinishing(false)
    }
  }

  const renderInput = (question) => {
    const value = getAnswerValue(question)

    switch (question.type) {
      case 'select':
        // Special-case: year_level rendered as badges instead of a <select>
        if (question.id === 'year_level') {
          return (
            <div className="flex flex-wrap gap-2">
              {question.options.map((option) => {
                const isSelected = value === option
                return (
                  <button
                    key={option}
                    type="button"
                    onClick={() => handleAnswerChange(question, option)}
                    className={
                      'px-3 py-1 rounded-full text-sm border transition-colors ' +
                      (isSelected
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50')
                    }
                  >
                    {option}
                  </button>
                )
              })}
            </div>
          )
        }
        // Fallback (no other selects right now)
        return (
          <select
            value={value}
            onChange={(e) => handleAnswerChange(question, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Select an option</option>
            {question.options.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        )

      case 'radio': {
        return (
          <div className="flex flex-wrap gap-2">
            {question.options.map((option) => {
              const isSelected = value === option.value
              return (
                <button
                  key={String(option.value)}
                  type="button"
                  onClick={() => handleAnswerChange(question, option.value)}
                  className={
                    'px-3 py-1 rounded-full text-sm border transition-colors ' +
                    (isSelected
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50')
                  }
                >
                  {option.label}
                </button>
              )
            })}
          </div>
        )
      }

      case 'checkbox': {
        const currentArray = Array.isArray(value) ? value : []
        return (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex flex-wrap gap-2">
                {question.options.map((option) => {
                  const checked = currentArray.includes(option)
                  return (
                    <button
                      key={option}
                      type="button"
                      onClick={() => {
                        const newArray = checked
                          ? currentArray.filter((item) => item !== option)
                          : [...currentArray, option]
                        handleAnswerChange(question, newArray)
                      }}
                      className={
                        'px-3 py-1 rounded-full text-sm border transition-colors ' +
                        (checked
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50')
                      }
                    >
                      {option}
                    </button>
                  )
                })}
              </div>
              {currentArray.length > 0 && (
                <button
                  type="button"
                  onClick={() => handleAnswerChange(question, [])}
                  className="text-xs text-gray-500 hover:text-gray-700 underline"
                >
                  Clear all
                </button>
              )}
            </div>
          </div>
        )
      }

      case 'number':
        return (
          <input
            type="number"
            min={question.min}
            max={question.max}
            value={value === '' ? '' : value}
            onChange={(e) => handleAnswerChange(question, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        )

      case 'text':
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleAnswerChange(question, e.target.value)}
            placeholder={question.placeholder}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        )

      case 'textarea':
        return (
          <textarea
            rows={4}
            value={value}
            onChange={(e) => handleAnswerChange(question, e.target.value)}
            placeholder={question.placeholder}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        )

      default:
        return null
    }
  }

  if (!lessonDesign) return null

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Progress summary */}
      <div className="space-y-2">
        <div className="flex justify-between text-sm text-gray-600">
          <span>
            {answeredCount} of {totalQuestions} questions answered
          </span>
          <span>{Math.round((answeredCount / totalQuestions) * 100)}% complete</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${(answeredCount / totalQuestions) * 100}%` }}
          />
        </div>
      </div>

      {/* Questions */}
      <div className="space-y-6">
        {visibleQuestions.map((question) => {
          const value = getAnswerValue(question)
          const isValid = validateQuestion(question, value)
          const showError = touched[question.id] && !isValid

          return (
            <div
              key={question.id}
              className="bg-white border border-gray-200 rounded-lg p-4 space-y-3"
            >
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">
                  {question.title} {question.required && <span className="text-red-500 ml-1">*</span>}
                </h2>
                {isValid && (
                  <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-500" />
                )}
              </div>
              {renderInput(question)}
              {showError && (
                <p className="text-sm text-red-600">
                  This question is required.
                </p>
              )}
            </div>
          )
        })}
      </div>

      {/* Save / Continue footer */}
      <div className="space-y-3">
        {saveError && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">
            {saveError}
          </div>
        )}
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500">
            {saving
              ? 'Saving changes...'
              : 'Changes are saved automatically as you answer.'}
          </div>
          <button
            onClick={handleFinish}
            disabled={!allValid || finishing}
            className="inline-flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {finishing ? (
              <>
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                <span>Continue to lesson plan</span>
              </>
            ) : (
              <>
                <SafeIcon icon={FiCheck} className="w-4 h-4" />
                <span>Continue to lesson plan</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default QuestionWizard